import {
  AUTO_SYNC,
  getAutoSyncDebounceMs,
  type AutoSyncReason,
} from "./offline-sync-policy";
import type {
  DrainShiftOfflineOrdersInput,
  DrainShiftOfflineOrdersResult,
} from "./drain";

export type AutoSyncScope = {
  branchId: string;
  deviceId: string;
  shiftId: string;
};

export type AutoSyncSkipReason =
  | "disabled"
  | "offline"
  | "missing_scope"
  | "debounced"
  | "already_running";

export type AutoSyncRunResult = DrainShiftOfflineOrdersResult & {
  reason: AutoSyncReason;
  startedAt: string;
  finishedAt: string;
};

export type AutoSyncStatus = {
  running: boolean;
  pendingReason: AutoSyncReason | null;
  lastReason?: AutoSyncReason;
  lastRunAt?: string;
  lastResult?: AutoSyncRunResult;
  lastSkipReason?: AutoSyncSkipReason;
  lastError?: string;
};

export type AutoSyncDeps = {
  drain: (
    input: DrainShiftOfflineOrdersInput,
  ) => Promise<DrainShiftOfflineOrdersResult>;
  isOnline: () => boolean;
  now: () => number;
  setTimeout: typeof globalThis.setTimeout;
  clearTimeout: typeof globalThis.clearTimeout;
  policy: typeof AUTO_SYNC;
};

async function defaultDrain(
  input: DrainShiftOfflineOrdersInput,
): Promise<DrainShiftOfflineOrdersResult> {
  const { drainShiftOfflineOrders } = await import("./drain");
  return drainShiftOfflineOrders(input);
}

const defaultDeps: AutoSyncDeps = {
  drain: defaultDrain,
  isOnline: () =>
    typeof navigator === "undefined" ? true : navigator.onLine,
  now: () => Date.now(),
  setTimeout: globalThis.setTimeout.bind(globalThis),
  clearTimeout: globalThis.clearTimeout.bind(globalThis),
  policy: AUTO_SYNC,
};

type DebounceHandle = ReturnType<typeof globalThis.setTimeout>;

export class AutoSyncScheduler {
  private scope: AutoSyncScope | null = null;
  private inFlight: Promise<AutoSyncRunResult | null> | null = null;
  private rerunAfterCurrent = false;
  private debounceTimers = new Map<AutoSyncReason, DebounceHandle>();
  private status: AutoSyncStatus = {
    running: false,
    pendingReason: null,
  };
  private listeners = new Set<(status: AutoSyncStatus) => void>();

  constructor(private readonly deps: AutoSyncDeps = defaultDeps) {}

  getStatus(): AutoSyncStatus {
    return { ...this.status };
  }

  subscribe(listener: (status: AutoSyncStatus) => void): () => void {
    this.listeners.add(listener);
    listener(this.getStatus());
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** @internal test reset */
  reset(): void {
    for (const handle of this.debounceTimers.values()) {
      this.deps.clearTimeout(handle);
    }
    this.debounceTimers.clear();
    this.scope = null;
    this.inFlight = null;
    this.rerunAfterCurrent = false;
    this.status = { running: false, pendingReason: null };
    this.emit();
  }

  setScope(scope: AutoSyncScope | null | undefined): void {
    if (scope?.branchId && scope.deviceId && scope.shiftId) {
      this.scope = scope;
    }
  }

  /** Clears pending debounced drains (e.g. before close-shift final sync). */
  cancelAllDebounced(): void {
    for (const handle of this.debounceTimers.values()) {
      this.deps.clearTimeout(handle);
    }
    this.debounceTimers.clear();
    if (this.status.pendingReason) {
      this.patchStatus({ pendingReason: null });
    }
  }

  getInFlightPromise(): Promise<AutoSyncRunResult | null> | null {
    return this.inFlight;
  }

  /**
   * Schedule or run a background drain. Returns immediately when debounced or
   * when another drain is already in progress (a follow-up run is queued).
   */
  request(
    reason: AutoSyncReason,
    scope?: AutoSyncScope | null,
  ): AutoSyncSkipReason | "scheduled" | "started" {
    if (scope) this.setScope(scope);

    if (!this.deps.policy.enabled) {
      this.patchStatus({ lastSkipReason: "disabled", lastReason: reason });
      return "disabled";
    }

    if (!this.deps.isOnline()) {
      this.patchStatus({ lastSkipReason: "offline", lastReason: reason });
      return "offline";
    }

    if (!this.scope) {
      this.patchStatus({ lastSkipReason: "missing_scope", lastReason: reason });
      return "missing_scope";
    }

    const debounceMs = getAutoSyncDebounceMs(reason, this.deps.policy);
    if (debounceMs > 0) {
      const existing = this.debounceTimers.get(reason);
      if (existing) this.deps.clearTimeout(existing);
      const handle = this.deps.setTimeout(() => {
        this.debounceTimers.delete(reason);
        void this.runNow(reason);
      }, debounceMs);
      this.debounceTimers.set(reason, handle);
      this.patchStatus({ pendingReason: reason, lastReason: reason });
      return "scheduled";
    }

    void this.runNow(reason);
    return this.status.running ? "already_running" : "started";
  }

  /** Run immediately (no debounce). Used for manual sync in later PRs. */
  async runNow(reason: AutoSyncReason): Promise<AutoSyncRunResult | null> {
    if (!this.deps.policy.enabled) {
      this.patchStatus({ lastSkipReason: "disabled", lastReason: reason });
      return null;
    }
    if (!this.deps.isOnline()) {
      this.patchStatus({ lastSkipReason: "offline", lastReason: reason });
      return null;
    }
    if (!this.scope) {
      this.patchStatus({ lastSkipReason: "missing_scope", lastReason: reason });
      return null;
    }

    if (this.inFlight) {
      this.rerunAfterCurrent = true;
      this.patchStatus({
        lastSkipReason: "already_running",
        pendingReason: reason,
        lastReason: reason,
      });
      return null;
    }

    const scope = this.scope;
    const startedAt = new Date(this.deps.now()).toISOString();
    this.patchStatus({
      running: true,
      pendingReason: null,
      lastReason: reason,
      lastRunAt: startedAt,
      lastSkipReason: undefined,
      lastError: undefined,
    });

    this.inFlight = this.executeDrain(reason, scope, startedAt);
    try {
      return await this.inFlight;
    } finally {
      this.inFlight = null;
      this.patchStatus({ running: false });
      if (this.rerunAfterCurrent) {
        this.rerunAfterCurrent = false;
        void this.runNow("interval");
      }
    }
  }

  private async executeDrain(
    reason: AutoSyncReason,
    scope: AutoSyncScope,
    startedAt: string,
  ): Promise<AutoSyncRunResult | null> {
    try {
      const drainResult = await this.deps.drain({
        branchId: scope.branchId,
        deviceId: scope.deviceId,
        shiftId: scope.shiftId,
      });
      const finishedAt = new Date(this.deps.now()).toISOString();
      const result: AutoSyncRunResult = {
        ...drainResult,
        reason,
        startedAt,
        finishedAt,
      };
      this.patchStatus({
        lastResult: result,
        lastError: undefined,
      });
      if (result.synced > 0) {
        const { runLocalCompactionAfterAutoSync } = await import(
          "./local-compaction"
        );
        await runLocalCompactionAfterAutoSync(scope.shiftId, result.synced);
      }
      return result;
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Auto sync drain failed";
      this.patchStatus({ lastError: message });
      console.warn("[auto-sync] drain failed", { reason, error });
      return null;
    }
  }

  private patchStatus(patch: Partial<AutoSyncStatus>): void {
    this.status = { ...this.status, ...patch };
    this.emit();
  }

  private emit(): void {
    const snapshot = this.getStatus();
    for (const listener of this.listeners) {
      listener(snapshot);
    }
  }
}

let scheduler: AutoSyncScheduler | null = null;

function getScheduler(): AutoSyncScheduler {
  if (!scheduler) scheduler = new AutoSyncScheduler();
  return scheduler;
}

/** Replace the global scheduler (tests only). */
export function __setAutoSyncSchedulerForTests(
  next: AutoSyncScheduler | null,
): void {
  scheduler = next;
}

export function getAutoSyncScheduler(): AutoSyncScheduler {
  return getScheduler();
}

export function requestAutoSync(
  reason: AutoSyncReason,
  scope?: AutoSyncScope | null,
): AutoSyncSkipReason | "scheduled" | "started" {
  return getScheduler().request(reason, scope);
}

export function runAutoSyncNow(
  reason: AutoSyncReason = "manual",
  scope?: AutoSyncScope | null,
): Promise<AutoSyncRunResult | null> {
  if (scope) getScheduler().setScope(scope);
  return getScheduler().runNow(reason);
}

export function getAutoSyncStatus(): AutoSyncStatus {
  return getScheduler().getStatus();
}

export function subscribeAutoSyncStatus(
  listener: (status: AutoSyncStatus) => void,
): () => void {
  return getScheduler().subscribe(listener);
}

/** Wait for any in-flight auto-sync drain before a close-shift drain. */
export async function waitForAutoSyncIdle(): Promise<void> {
  const scheduler = getScheduler();
  scheduler.cancelAllDebounced();
  const inFlight = scheduler.getInFlightPromise();
  if (inFlight) await inFlight;
}

export type AutoSyncScopeSource = {
  branchId: string;
  deviceId: string;
  shiftId: string;
};

export function toAutoSyncScope(source: AutoSyncScopeSource): AutoSyncScope {
  return source;
}

export function requestAutoSyncAfterComplete(
  source: AutoSyncScopeSource,
): AutoSyncSkipReason | "scheduled" | "started" {
  return requestAutoSync("after_complete", toAutoSyncScope(source));
}

export function requestAutoSyncAfterAdjustment(
  source: AutoSyncScopeSource,
): AutoSyncSkipReason | "scheduled" | "started" {
  return requestAutoSync("after_adjustment", toAutoSyncScope(source));
}
